import { Link, createFileRoute } from "@tanstack/react-router";
import { Shell } from "~/components/site-shell";
import { Reveal } from "~/components/reveal";
import { Arrow, HostingStackIcon, RuntimeMark } from "~/components/icons";
import { HomeHero } from "~/components/rotating-hero";
import { HomeProductHighlights } from "~/components/product";
import { AvailabilityBadge } from "~/components/regions";
import { breadcrumbs, jsonLd, seoHead } from "~/config/seo";

export const Route = createFileRoute("/")({
  head: () => ({
    ...seoHead({
      title: "WordPress, PHP, Node.js & Python Hosting | HostRight",
      description:
        "From just $2.99/month, one hosting plan includes WordPress, PHP, Node.js, Python, 20+ applications, databases, Redis cache, email, backups, and scheduled jobs.",
    }),
    scripts: [jsonLd(breadcrumbs([{ name: "Home", path: "/" }]))],
  }),
  component: Home,
});

type HostingApplication = {
  name: string;
  href: string;
};

type InstallGroup = {
  title: string;
  apps: readonly HostingApplication[];
};

type HostingPath = {
  mark: "php" | "node" | "python" | "install";
  title: string;
  description: string;
  cta: string;
  href: string;
  applications?: readonly HostingApplication[];
  installGroups?: readonly InstallGroup[];
  wide?: boolean;
};

const hostingPaths: readonly HostingPath[] = [
  {
    mark: "php",
    title: "PHP",
    description:
      "Give a custom website, client portal or business tool a dependable home. Start with a familiar PHP project, then keep growing it without assembling a stack of separate services.",
    cta: "Explore PHP hosting",
    href: "/knowledge-base/application-hosting/php",
    applications: [
      {
        name: "Laravel",
        href: "/knowledge-base/application-hosting/php/laravel",
      },
      {
        name: "Symfony",
        href: "/knowledge-base/application-hosting/php/symfony",
      },
      {
        name: "CodeIgniter",
        href: "/knowledge-base/application-hosting/php/codeigniter",
      },
      { name: "Yii", href: "/knowledge-base/application-hosting/php/yii" },
    ],
  },
  {
    mark: "node",
    title: "Node.js",
    description:
      "Bring a JavaScript product, customer dashboard or service online in a managed home that lets you focus on what you are building, not the day-to-day upkeep around it.",
    cta: "Explore Node.js hosting",
    href: "/knowledge-base/application-hosting/nodejs",
    applications: [
      {
        name: "Express",
        href: "/knowledge-base/application-hosting/nodejs/express",
      },
      {
        name: "Next.js",
        href: "/knowledge-base/application-hosting/nodejs/nextjs",
      },
      { name: "Nuxt", href: "/knowledge-base/application-hosting/nodejs/nuxt" },
      {
        name: "NestJS",
        href: "/knowledge-base/application-hosting/nodejs/nestjs",
      },
    ],
  },
  {
    mark: "python",
    title: "Python",
    description:
      "Take a Python project from a promising idea to a public application with a clear place to run it. It is well suited to teams creating thoughtful products and internal tools.",
    cta: "Explore Python hosting",
    href: "/knowledge-base/application-hosting/python",
    applications: [
      {
        name: "Django",
        href: "/knowledge-base/application-hosting/python/django",
      },
      {
        name: "Flask",
        href: "/knowledge-base/application-hosting/python/flask",
      },
      {
        name: "Pyramid",
        href: "/knowledge-base/application-hosting/python/pyramid",
      },
      {
        name: "Bottle",
        href: "/knowledge-base/application-hosting/python/bottle",
      },
    ],
  },
  {
    mark: "install",
    title: "One-click applications",
    description:
      "Start a website, store, community or business tool in a few guided steps. Popular open-source applications are ready when you are.",
    href: "/knowledge-base/application-hosting/one-click",
    cta: "Explore all one-click applications",
    wide: true,
    installGroups: [
      {
        title: "Websites",
        apps: [
          {
            name: "WordPress",
            href: "/knowledge-base/application-hosting/one-click/wordpress",
          },
          {
            name: "WooCommerce",
            href: "/knowledge-base/application-hosting/one-click/woocommerce",
          },
          {
            name: "Joomla",
            href: "/knowledge-base/application-hosting/one-click/joomla",
          },
          {
            name: "Open Real Estate",
            href: "/knowledge-base/application-hosting/one-click/open-real-estate",
          },
        ],
      },
      {
        title: "Stores",
        apps: [
          {
            name: "PrestaShop",
            href: "/knowledge-base/application-hosting/one-click/prestashop",
          },
          {
            name: "AbanteCart",
            href: "/knowledge-base/application-hosting/one-click/abantecart",
          },
          {
            name: "Dolphin",
            href: "/knowledge-base/application-hosting/one-click/dolphin",
          },
        ],
      },
      {
        title: "Community",
        apps: [
          {
            name: "phpBB",
            href: "/knowledge-base/application-hosting/one-click/phpbb",
          },
          {
            name: "MyBB",
            href: "/knowledge-base/application-hosting/one-click/mybb",
          },
          {
            name: "SMF",
            href: "/knowledge-base/application-hosting/one-click/smf",
          },
          {
            name: "Moodle",
            href: "/knowledge-base/application-hosting/one-click/moodle",
          },
        ],
      },
      {
        title: "Teams",
        apps: [
          {
            name: "Nextcloud",
            href: "/knowledge-base/application-hosting/one-click/nextcloud",
          },
          {
            name: "Feng Office",
            href: "/knowledge-base/application-hosting/one-click/feng-office",
          },
          {
            name: "LimeSurvey",
            href: "/knowledge-base/application-hosting/one-click/limesurvey",
          },
        ],
      },
      {
        title: "Business",
        apps: [
          {
            name: "Invoice Ninja",
            href: "/knowledge-base/application-hosting/one-click/invoice-ninja",
          },
          {
            name: "Akaunting",
            href: "/knowledge-base/application-hosting/one-click/akaunting",
          },
          {
            name: "Dolibarr",
            href: "/knowledge-base/application-hosting/one-click/dolibarr",
          },
          {
            name: "Kimai",
            href: "/knowledge-base/application-hosting/one-click/kimai",
          },
        ],
      },
      {
        title: "Growth",
        apps: [
          {
            name: "Matomo",
            href: "/knowledge-base/application-hosting/one-click/matomo",
          },
          {
            name: "Mautic",
            href: "/knowledge-base/application-hosting/one-click/mautic",
          },
          {
            name: "SEO Panel",
            href: "/knowledge-base/application-hosting/one-click/seo-panel",
          },
          {
            name: "Wallabag",
            href: "/knowledge-base/application-hosting/one-click/wallabag",
          },
        ],
      },
    ],
  },
] as const;

const connectedServices = [
  { title: "Professional email", type: "email", position: "top" },
  { title: "DNS and SSL", type: "domain", position: "left" },
  { title: "MariaDB", type: "database", position: "right" },
  { title: "Backups", type: "backup", position: "bottom-left" },
  { title: "Redis", type: "redis", position: "bottom-right" },
  { title: "Scheduled jobs", type: "schedule", position: "bottom" },
] as const;

const howItWorksSteps = [
  {
    number: "01",
    title: "Choose your hosting",
    description:
      "Select a plan based on the websites, databases, mailboxes and storage you need.",
    icon: "database",
  },
  {
    number: "02",
    title: "Connect your domain",
    description:
      "Use an existing domain or find a new one, then manage DNS from the same account.",
    icon: "domain",
  },
  {
    number: "03",
    title: "Launch your project",
    description:
      "Install WordPress and popular software or deploy a compatible application.",
    icon: "schedule",
  },
  {
    number: "04",
    title: "Keep it running",
    description:
      "Manage email, SSL, databases, backups and scheduled tasks from one place.",
    icon: "backup",
  },
] as const;

function ServiceNode({
  title,
  type,
}: {
  title: string;
  type: "email" | "database" | "domain" | "backup" | "schedule" | "redis";
}) {
  return (
    <div className="flex min-w-36 items-center gap-2 rounded-2xl border border-[#D7DED8] bg-cream px-3 py-2.5 shadow-sm">
      <HostingStackIcon type={type} />
      <span className="text-sm font-semibold text-ink/80">{title}</span>
    </div>
  );
}

function Home() {
  return (
    <Shell>
      <section className="paper-grid overflow-hidden bg-cream">
        <HomeHero />
      </section>

      <section className="border-y border-ink/10 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-12">
          <Reveal>
            <h2 className="display max-w-2xl text-5xl font-bold leading-[.93] lg:text-[58px]">
              Choose an application or bring your own
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-ink/65">
              Host familiar websites and custom web applications without piecing
              together separate services. PHP, Node.js and Python support come
              with email, databases, SSL, DNS and backups already included.
            </p>
            <Link
              to="/knowledge-base/$"
              params={{ _splat: "application-hosting" }}
              className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-moss"
            >
              Explore supported applications <Arrow />
            </Link>
          </Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-3 lg:gap-5">
            {hostingPaths.map(
              ({
                mark,
                title,
                description,
                cta,
                href,
                applications,
                installGroups,
                wide,
              }) => (
                <Reveal
                  key={title}
                  className={wide ? "md:col-span-3" : undefined}
                >
                  <article
                    className={`min-h-[280px] rounded-[1.5rem] border border-ink/10 bg-cream p-6 shadow-[0_1px_0_rgba(13,43,34,.03)] lg:p-7 ${wide ? "flex flex-col md:grid md:grid-cols-[minmax(0,.62fr)_minmax(0,1.38fr)] md:items-start md:gap-8" : "flex h-full flex-col"}`}
                  >
                    <div
                      className={`flex flex-col ${wide ? "" : "h-full flex-1"}`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <h3 className="text-xl font-bold tracking-[-0.035em]">
                            {title}
                          </h3>
                          <a
                            href={href}
                            className="group mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-ink/60 transition hover:text-moss focus-visible:outline-3 focus-visible:outline-leaf focus-visible:outline-offset-3"
                          >
                            {cta} <Arrow />
                          </a>
                        </div>
                        <RuntimeMark type={mark} />
                      </div>
                      <p className="mt-5 text-sm font-medium leading-6 text-[#2D3748]">
                        {description}
                      </p>
                      {!wide && (
                        <nav
                          className="mt-auto flex flex-wrap gap-x-4 gap-y-2 pt-7 text-sm"
                          aria-label={`${title} guides`}
                        >
                          {applications?.map((application) => (
                            <a
                              key={application.href}
                              href={application.href}
                              className="application-catalogue-link font-semibold text-[#2D3748]/80 transition-colors hover:text-moss focus-visible:text-moss"
                            >
                              {application.name}
                            </a>
                          ))}
                        </nav>
                      )}
                    </div>
                    {wide ? (
                      <div className="mt-8 grid gap-4 sm:grid-cols-2 md:mt-0 lg:grid-cols-3">
                        {installGroups?.map((group) => (
                          <div
                            key={group.title}
                            className="rounded-xl border border-moss/10 bg-white/85 p-4"
                          >
                            <p className="text-[0.68rem] font-extrabold uppercase tracking-[0.14em] text-ink/55">
                              {group.title}
                            </p>
                            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm leading-6">
                              {group.apps.map((app) => (
                                <a
                                  key={app.href}
                                  href={app.href}
                                  className="application-catalogue-link font-semibold text-[#2D3748]/80 transition-colors hover:text-moss focus-visible:text-moss"
                                >
                                  {app.name}
                                </a>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </article>
                </Reveal>
              ),
            )}
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-cream">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 lg:grid-cols-[.8fr_1.2fr] lg:gap-16 lg:px-12">
          <Reveal>
            <h2 className="display max-w-xl text-5xl font-bold leading-[.93] lg:text-[58px]">
              Your whole hosting stack, already connected.
            </h2>
            <p className="mt-6 max-w-lg text-lg leading-8 text-ink/70">
              Websites, applications, email, databases, domains, SSL and backups
              live together in one managed account. No separate services to
              stitch together.
            </p>
            <AvailabilityBadge />
          </Reveal>
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] border border-ink/10 bg-white p-5 shadow-[0_18px_38px_rgba(13,43,34,.08)] sm:p-7">
              <div className="grid gap-3 sm:hidden">
                <div className="rounded-2xl bg-ink p-5 text-cream">
                  <p className="eyebrow text-leaf">One account</p>
                  <p className="mt-3 text-lg font-bold">
                    Websites · Apps · Email · Domains
                  </p>
                </div>
                {connectedServices.map(({ title, type }) => (
                  <div
                    key={title}
                    className="flex items-center gap-3 rounded-xl bg-cream p-4"
                  >
                    <HostingStackIcon type={type} />
                    <p className="font-semibold">{title}</p>
                  </div>
                ))}
              </div>
              <div className="relative hidden min-h-[390px] sm:block">
                <svg
                  aria-hidden="true"
                  viewBox="0 0 640 390"
                  className="absolute inset-0 size-full text-moss/25"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M320 180 320 58M320 180 164 100M320 180 476 100M320 180 164 260M320 180 476 260M320 180 320 332"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeDasharray="4 5"
                  />
                </svg>
                <div className="absolute left-1/2 top-2 -translate-x-1/2">
                  <ServiceNode title="Professional email" type="email" />
                </div>
                <div className="absolute left-[8%] top-[25%] -translate-y-1/2"></div>
                <div className="absolute left-[8%] top-[25%] -translate-y-1/2">
                  <ServiceNode title="DNS and SSL" type="domain" />
                </div>
                <div className="absolute right-[8%] top-[25%] -translate-y-1/2">
                  <ServiceNode title="MariaDB" type="database" />
                </div>
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-ink px-5 py-4 text-center text-cream shadow-[0_12px_25px_rgba(13,43,34,.24)] ring-4 ring-leaf/60">
                  <p className="eyebrow text-leaf">One account</p>
                  <p className="mt-2 text-base font-bold">HostRight account</p>
                  <p className="mt-1.5 text-xs text-cream/70">
                    Websites · Apps · Email · Domains
                  </p>
                </div>
                <div className="absolute bottom-20 left-[8%]">
                  <ServiceNode title="Backups" type="backup" />
                </div>
                <div className="absolute bottom-20 right-[8%]">
                  <ServiceNode title="Redis" type="redis" />
                </div>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
                  <ServiceNode title="Scheduled jobs" type="schedule" />
                </div>
              </div>
            </div>
            <p className="mt-5 text-xs leading-5 text-ink/55">
              Included services vary by plan and remain subject to storage,
              resource and fair-use limits.
            </p>
          </Reveal>
        </div>
      </section>

      <HomeProductHighlights />

      <section className="border-y border-ink/10 bg-cream">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-12">
          <Reveal>
            <h2 className="display max-w-2xl text-5xl font-bold leading-[.95] lg:text-[58px]">
              Four clear steps. No server setup.
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-ink/65">
              Start with a ready-made application or bring your own project.
              HostRight gives you the tools and guidance to put it online
              without maintaining a VPS.
            </p>
          </Reveal>

          <div className="relative mt-11">
            <div className="absolute left-[12.5%] right-[12.5%] top-[1.4rem] hidden h-px bg-moss/20 lg:block" />
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-7">
              {howItWorksSteps.map((step) => (
                <Reveal key={step.number}>
                  <article className="relative text-left">
                    <div className="relative z-10 flex items-center gap-3 bg-cream pr-3 lg:w-fit">
                      <span className="grid size-11 shrink-0 place-items-center rounded-full border border-moss/15 bg-white shadow-[0_2px_6px_rgba(13,43,34,.06)]">
                        <HostingStackIcon type={step.icon} />
                      </span>
                      <h3 className="text-lg font-bold tracking-[-0.03em]">
                        {step.title}
                      </h3>
                    </div>
                    <p className="mt-4 max-w-[17rem] text-sm leading-6 text-ink/65">
                      {step.description}
                    </p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal>
            <div className="mt-10 flex flex-col justify-between gap-6 rounded-[1.5rem] bg-ink px-6 py-5 text-cream shadow-[0_14px_28px_rgba(13,43,34,.16)] sm:flex-row sm:items-center sm:px-8">
              <div>
                <p className="font-bold">yourproject.com</p>
                <p className="mt-1 text-sm text-cream/65">
                  Online · SSL active · Backup complete
                </p>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm font-bold">
                <a href="/knowledge-base/getting-started" className="text-leaf">
                  See how HostRight works
                </a>
                <Link
                  to="/knowledge-base/$"
                  params={{ _splat: "application-hosting" }}
                  className="text-cream"
                >
                  Browse deployment guides
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="get-started" className="border-t border-ink/10 bg-ink">
        <div className="mx-auto max-w-[1280px] px-5 py-16 lg:px-12 lg:py-20">
          <div className="cta-banner relative isolate overflow-hidden rounded-[2rem] border border-leaf/20 bg-[#0f2820] px-7 py-16 text-center text-cream shadow-[0_24px_60px_rgba(13,43,34,.18)] sm:px-12 lg:py-[84px]">
            <div
              aria-hidden="true"
              className="cta-glow absolute -left-24 -top-32 -z-10 size-[28rem] rounded-full bg-leaf/20 blur-3xl"
            />
            <div
              aria-hidden="true"
              className="cta-glow absolute -bottom-48 -right-20 -z-10 size-[30rem] rounded-full bg-moss/50 blur-3xl"
            />
            <svg
              aria-hidden="true"
              viewBox="0 0 920 360"
              className="pointer-events-none absolute inset-0 -z-10 size-full text-leaf/15"
              preserveAspectRatio="none"
            >
              <path
                d="M-40 285C130 200 180 315 340 228S625 45 960 122"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeDasharray="5 9"
              />
              <path
                d="M-30 320C140 238 205 350 370 258S655 92 950 160"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              />
              <circle cx="692" cy="94" r="4" fill="currentColor" />
              <circle
                cx="692"
                cy="94"
                r="16"
                fill="none"
                stroke="currentColor"
              />
            </svg>
            <div className="cta-badge cta-badge-top" aria-hidden="true">
              <span className="size-2 rounded-full bg-leaf shadow-[0_0_10px_#a8f35a]" />
              99% target uptime
            </div>
            <div className="cta-badge cta-badge-bottom" aria-hidden="true">
              <span className="text-leaf">✦</span>
              Instant deploy
            </div>
            <h2 className="display relative mx-auto max-w-[720px] text-5xl font-extrabold leading-[.91] tracking-[-0.065em] lg:text-[60px]">
              Put your website in good hands.
            </h2>
            <p className="relative mx-auto mt-6 max-w-xl text-lg leading-8 text-cream/85">
              Choose a plan and get your next website online with hosting that
              stays simple.
            </p>
            <Link
              to="/products/web-hosting/shared-hosting"
              className="button-primary relative mt-9 inline-flex rounded-full px-8 py-4 text-base font-bold shadow-[0_8px_22px_rgba(0,0,0,.22)] transition duration-200 motion-safe:hover:-translate-y-1 motion-safe:hover:scale-[1.02] motion-safe:hover:shadow-[0_12px_28px_rgba(168,243,90,.22)] focus-visible:outline-3 focus-visible:outline-leaf focus-visible:outline-offset-3"
            >
              View hosting plans
            </Link>
          </div>
        </div>
      </section>
    </Shell>
  );
}
